import type { AlbumDocument } from './schema';
import { applyCommand, type Command } from './ops';

export const HISTORY_LIMIT = 200;

/**
 * Snapshot-based undo/redo. Documents are immutable with structural sharing, so keeping up to
 * HISTORY_LIMIT snapshots is cheap. Consecutive `apply` calls with the same `coalesceKey`
 * (e.g. one drag gesture) collapse into a single undo step.
 */
export class History {
  #past: AlbumDocument[] = [];
  #future: AlbumDocument[] = [];
  #present: AlbumDocument;
  #lastKey: string | undefined;

  constructor(initial: AlbumDocument) {
    this.#present = initial;
  }

  get current(): AlbumDocument {
    return this.#present;
  }
  get canUndo(): boolean {
    return this.#past.length > 0;
  }
  get canRedo(): boolean {
    return this.#future.length > 0;
  }
  get length(): number {
    return this.#past.length;
  }

  apply(cmd: Command, opts: { coalesceKey?: string } = {}): AlbumDocument {
    const next = applyCommand(this.#present, cmd);
    if (next === this.#present) return next;
    const key = opts.coalesceKey;
    if (key === undefined || key !== this.#lastKey) {
      this.#past.push(this.#present);
      if (this.#past.length > HISTORY_LIMIT) this.#past.shift();
    }
    this.#lastKey = key;
    this.#present = next;
    this.#future = [];
    return next;
  }

  /** Ends the current coalescing run so the next `apply` with the same key starts a new step. */
  endCoalescing(): void {
    this.#lastKey = undefined;
  }

  undo(): AlbumDocument | null {
    const prev = this.#past.pop();
    if (!prev) return null;
    this.#future.push(this.#present);
    this.#present = prev;
    this.#lastKey = undefined;
    return prev;
  }

  redo(): AlbumDocument | null {
    const next = this.#future.pop();
    if (!next) return null;
    this.#past.push(this.#present);
    this.#present = next;
    this.#lastKey = undefined;
    return next;
  }

  reset(doc: AlbumDocument): void {
    this.#past = [];
    this.#future = [];
    this.#present = doc;
    this.#lastKey = undefined;
  }
}
